import { useCallback, useEffect } from 'react'
// eslint-disable-next-line import/no-named-as-default
import i18n from 'i18next'
import dayjs from 'dayjs'
import { dateLocales } from '@/features/base/language-management/locales'
import { useAppSelector } from '@/store' // assumed userId is in store
import { localeService } from '@/features/base/language-management/services/LocaleService'
import { PageType } from '@/constants/pageTypes'
import { logger } from '@/utils/logger'

function useLocale() {
    const locale = useAppSelector((state) => state.locale.currentLang)

    useEffect(() => {
        const formattedLang = locale.replace(/-([a-z])/g, (g) =>
            g[1].toUpperCase(),
        )

        if (locale !== i18n.language) {
            i18n.changeLanguage(formattedLang)
        }

        dateLocales[formattedLang]().then(() => {
            dayjs.locale(formattedLang)
        })
    }, [locale])

    const getCacheLanguage = useCallback(async (languageId: number) => {
        // eslint-disable-next-line no-useless-catch
        try {
            logger.debug('Fetching cache language preference:', { languageId })
            const response =
                await localeService.getCacheLanguagePreference(languageId)
            logger.debug('Cache language preference fetched successfully:', {
                languageId,
                responseLength: response?.length,
            })
            return response
        } catch (error) {
            logger.error('Failed to fetch cache language preference:', {
                languageId,
                error: error instanceof Error ? error.message : String(error),
                stack: error instanceof Error ? error.stack : undefined,
            })
            throw error
        }
    }, [])

    // Generic method to get language tokens for any page
    const getPageLanguage = useCallback(
        async (languageId: number, pageType: PageType) => {
            // eslint-disable-next-line no-useless-catch
            try {
                logger.debug('Fetching page language preference:', {
                    languageId,
                    pageType,
                })
                const response = await localeService.getPageLanguagePreference(
                    languageId,
                    pageType,
                )
                logger.debug('Page language preference fetched successfully:', {
                    languageId,
                    pageType,
                    responseLength: response?.length,
                })
                return response
            } catch (error) {
                logger.error('Failed to fetch page language preference:', {
                    languageId,
                    pageType,
                    error:
                        error instanceof Error ? error.message : String(error),
                    stack: error instanceof Error ? error.stack : undefined,
                })
                throw error
            }
        },
        [],
    )

    return { locale, getCacheLanguage, getPageLanguage }
}

export default useLocale
